import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { ClassValue } from "clsx";
import { type HTMLAttributes, createElement, forwardRef } from "react";
import { cn } from "../../..";
import { type IconProps, icon } from "../interactive/Icon";
import { VariantMapping, type Variants } from "./Variants";

type TypographyComponentProps = HTMLAttributes<HTMLHeadingElement> &
	HTMLAttributes<HTMLParagraphElement> &
	HTMLAttributes<HTMLSpanElement>;

interface TypographyProps extends TypographyComponentProps, IconProps {
	variant?: Variants;
	component?: (typeof VariantMapping)[keyof typeof VariantMapping];
	color?:
		| "text-dark"
		| "text-primary-medium"
		| "text-white"
		| "text-shades-dark"
		| "text-secondary";
	size?: "text-xs" | "text-sm" | "text-base" | "text-lg" | "text-xl";
	spacing?: boolean;
}

export const Typography = forwardRef<TypographyComponentProps, TypographyProps>(
	function Ref(props, ref) {
		const {
			variant = "paragraph",
			component,
			color = "text-dark",
			size,
			spacing,
			starticon,
			endicon,
			iconColor,
			iconSize,
			children,
			className,
			...remainingProps
		} = props;

		const Component = component ?? VariantMapping[variant];

		const SpacingStyle: ClassValue = {
			"first:mt-0 mb-2.5": spacing,
			"mt-[2.125rem] ": variant === "h1" && spacing,
			"mt-8": variant === "h2" && spacing,
			"mt-[1.875rem]": variant === "h3" && spacing,
			"mt-6": variant === "h4" && spacing,
			"mt-5": variant === "h5" && spacing,
			"mt-4": variant === "h6" && spacing,
		};

		const VariantStyle: ClassValue = {
			"font-primary font-extrabold text-3xl": variant === "h1",
			"font-primary font-extrabold text-2xl": variant === "h2",
			"font-primary font-extrabold text-xl": variant === "h3",
			"font-primary font-extrabold text-lg": variant === "h4",
			"font-primary font-extrabold text-base": variant === "h5",
			"font-primary font-extrabold text-mdsm": variant === "h6",
			"font-sans": variant === "paragraph",
			"font-sans font-bold": variant === "paragraph-bold",
			"font-sans italic": variant === "paragraph-italic",
			"font-primary font-semibold": variant === "paragraph-primary-semibold",
			"font-primary": variant === "paragraph-primary",
			"font-primary italic": variant === "paragraph-primary-italic",
			"font-primary font-extrabold": variant === "paragraph-primary-extrabold",
		};

		const element = createElement(
			Component,
			{
				...remainingProps,
				className: cn("break-words", VariantStyle, SpacingStyle, color, size, className),
				ref,
			},
			<>
				{starticon && (
					<FontAwesomeIcon
						className={cn(icon({ color: iconColor }), { "mr-2": children })}
						icon={starticon}
						size={iconSize === "default" ? undefined : (iconSize ?? "sm")}
						fixedWidth
					/>
				)}

				{children}

				{endicon && (
					<FontAwesomeIcon
						className={cn(icon({ color: iconColor }), { "ml-2": children })}
						icon={endicon}
						size={iconSize === "default" ? undefined : (iconSize ?? "sm")}
						fixedWidth
					/>
				)}
			</>,
		);

		return element;
	},
);
